import React from 'react';
import styled from 'styled-components';
import {
   internetLine,
   outageLine,
   restartLine,
   parseEvents,
   friendlyWhen,
   isReportStale,
   reportedAgo,
   tunnelCard,
   blameUs,
   type RoadEventRow,
   type RawDiag,
} from './deviceDiagText';
import { uptime } from '@/utils/uptime';
import { antenna as deriveAntenna } from '@/utils/linkHealth';

/**
 * What the ROUTER says happened to it — never the NodeMCU; they fail for
 * completely different reasons and an operator cannot tell which is meant.
 *
 * Until now a vendo could only say "Offline", which is the same word for a shop
 * that lost power, a shop whose internet died, a box that froze, and a box that
 * is working perfectly but cannot reach us. Operators guessed, rebooted, and in
 * two cases shipped the vendo back to the shop from an island.
 *
 * Shape (Ray's review, 2026-08-11): the last outage is a BANNER at the top,
 * because it is the first thing anyone opening this tab wants to know. Then the
 * two live states as cards side by side. Then the timeline.
 *
 * The outage ALSO stays in the timeline at the moment it happened. That is not
 * duplication for its own sake: a banner cannot show where the gap falls between
 * the events either side of it, and the absence of that was what made the screen
 * read as a contradiction ("4 hrs ago last connected, but 8 hours down?").
 */
export default function ConnectionHistory({
   diag,
   lastSeenAt,
   nodeMcu,
   apBssids,
   roadHistory,
}: {
   diag?: RawDiag | null;
   /** The NodeMCU, for the antenna card. Optional so a device without one
    *  simply does not get that card. */
   nodeMcu?: {
      bssid?: string;
      connMode?: string;
      pinFellBack?: boolean;
   } | null;
   /** The router's OWN radios, so we can say whether the NodeMCU is on one. */
   apBssids?: { bssid: string; freq: number }[];
   /** When the ROUTER last spoke to us. Decides whether the report below is the
    *  present tense or a snapshot from before the box went quiet. */
   lastSeenAt?: string | null;
   /** When this router changed how it reaches us. Admin path only — the
    *  operator API does not serve it. Absent is normal, not an error. */
   roadHistory?: RoadEventRow[];
}) {
   if (!diag) {
      return (
         <Empty>
            This box has not reported yet. It needs a firmware update before it
            can explain what happens to it.
         </Empty>
      );
   }

   // Three sources on ONE timeline: the box's own transitions, the repairs it
   // attempted, and the times it changed how it reaches us. The last two were
   // invisible until now — the repair record arrived on every heartbeat and was
   // discarded, and a road switch left no trace at all once the router came
   // home.
   const events = parseEvents(
      diag.events,
      diag.outage,
      diag.actions,
      roadHistory
   );
   const banner = outageLine(diag.outage);
   const restarts = restartLine(diag.reboots24h);
   // 🔴 The box may have gone quiet since it sent this. If so NOTHING in the
   // report is the present tense, and saying "Connected to SmartFi" would be a
   // lie of four hours' standing (Ray's bench box, 2026-08-11).
   const stale = isReportStale(lastSeenAt);
   const silentSince = lastSeenAt ? new Date(lastSeenAt) : null;
   // 🔴 This used to be `(diag.hsAge ?? -1) < 0`, which read the server's
   // "the router did not report one" default as a dead tunnel — 79 live,
   // heartbeating vendos rendered red "Not reachable" on 2026-08-23. Unknown
   // is now its own state and its own colour.
   const tunnel = tunnelCard(diag.hsAge, stale);
   const inetDown = diag.inet === 'down';
   // The combination worth shouting about: we cannot see it, but the shop is
   // fine. That is the difference between "your internet is down" and "our
   // system is the problem", and it is the whole reason for this panel. Only
   // ever said when the tunnel is KNOWN dead — never on an unknown.
   const usNotThem = blameUs(diag.hsAge, diag.inet, stale);
   // Same helper the device list and the admin fleet table use, so "offline 3d"
   // means one thing everywhere an operator or admin can read it.
   const up = uptime({
      lastSeenAt,
      events: diag.events,
      outage: diag.outage,
   });
   const ant = deriveAntenna(nodeMcu ?? undefined, { apBssids });

   return (
      <div>
         {banner && (
            <Banner $tone={diag.outage?.includes('watchdog') ? 'warn' : 'bad'}>
               <BannerTitle>Last outage</BannerTitle>
               <BannerText>{banner}</BannerText>
               {restarts && <BannerSub>{restarts}</BannerSub>}
            </Banner>
         )}

         {/* Counted here rather than in CSS: these two cards are conditional,
             so only the render site knows how many columns are needed. */}
         <Cards $count={2 + (up ? 1 : 0) + (ant && ant.state !== 'unknown' ? 1 : 0)}>
            <Card $tone={tunnel.tone}>
               <CardLabel>SmartFi connection</CardLabel>
               <CardValue>{tunnel.text}</CardValue>
               <CardNote>
                  {stale && silentSince
                     ? `Silent since ${friendlyWhen(silentSince)}`
                     : reportedAgo(lastSeenAt)}
               </CardNote>
            </Card>
            <Card
               $tone={
                  stale
                     ? 'unknown'
                     : inetDown
                       ? 'bad'
                       : diag.inet === 'up'
                         ? 'ok'
                         : 'unknown'
               }
            >
               <CardLabel>Internet at the shop</CardLabel>
               <CardValue>
                  {stale ? 'Unknown' : internetLine(diag.inet)}
               </CardValue>
               <CardNote>
                  {stale
                     ? 'The router cannot tell us while it is unreachable'
                     : reportedAgo(lastSeenAt)}
               </CardNote>
            </Card>
            {/* How long it has held this state. The two cards beside it say WHAT
                is true; this one says FOR HOW LONG, which is the difference
                between "it just blipped" and "this shop has been dark all day".
                Hidden when the box reports no history to anchor it — an invented
                "up 0m" would be worst on exactly the boxes we know least about. */}
            {up && (
               <Card $tone={up.down ? 'bad' : 'ok'}>
                  <CardLabel>{up.down ? 'Offline for' : 'Online for'}</CardLabel>
                  <CardValue>{up.duration}</CardValue>
                  <CardNote>{up.title}</CardNote>
               </Card>
            )}
            {/* WHICH ANTENNA the NodeMCU is on. A wireless box joins by WiFi
                NAME alone and takes whichever access point is loudest, without
                checking whose it is — so it can end up on the operator's own
                extender, or on a neighbour using the same name, and look
                perfectly healthy while being unreachable.

                ⚠️ The wording never says "captured". An extender is separate
                hardware with its own address, so a completely legitimate shop
                reads as "another antenna" — and telling an operator they have
                been hacked because they bought a range extender is worse than
                saying nothing. Hidden entirely when we cannot tell. */}
            {ant && ant.state !== 'unknown' && (
               <Card $tone={ant.state === 'home' || ant.state === 'external' ? 'ok' : 'bad'}>
                  <CardLabel>NodeMCU antenna</CardLabel>
                  <CardValue>{ant.label}</CardValue>
                  <CardNote>{ant.detail}</CardNote>
               </Card>
            )}
         </Cards>

         {usNotThem && (
            <Notice>
               The shop's internet is working. This vendo cannot reach SmartFi,
               and that is on our side — not the customer's line.
            </Notice>
         )}

         {events.length > 0 ? (
            <>
               <SubTitle>
                  What happened, most recent first
                  {stale && ' — up to when it went quiet'}
               </SubTitle>
               <List>
                  {events.map((e) => (
                     <Item key={`${e.at.getTime()}-${e.text}`}>
                        <Dot $tone={e.tone} />
                        <When>{friendlyWhen(e.at)}</When>
                        <What $tone={e.tone}>{e.text}</What>
                     </Item>
                  ))}
               </List>
            </>
         ) : (
            !banner && (
               <Empty>Nothing to report — this box has not had a problem.</Empty>
            )
         )}
      </div>
   );
}

const TONES = {
   ok: '#16a34a',
   warn: '#d97706',
   bad: '#c0392b',
   unknown: '#9ca3af',
} as const;
type Tone = keyof typeof TONES;

const Banner = styled.div<{ $tone: Tone }>`
   border-radius: 10px;
   padding: 12px 14px;
   margin-bottom: 16px;
   border: 1px solid ${({ $tone }) => TONES[$tone]}44;
   background: ${({ $tone }) => TONES[$tone]}14;
   border-left: 4px solid ${({ $tone }) => TONES[$tone]};
`;
const BannerTitle = styled.div`
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   opacity: 0.65;
   margin-bottom: 3px;
`;
const BannerText = styled.div`
   font-size: 14px;
   font-weight: 600;
   line-height: 1.45;
`;
const BannerSub = styled.div`
   margin-top: 4px;
   font-size: 13px;
   opacity: 0.8;
`;

const Cards = styled.div<{ $count: number }>`
   display: grid;
   /* 🔴 The column count is COUNTED AND PASSED IN, not inferred.
      auto-fit with a minmax() looked right and was tried first — it still
      rendered three across with the fourth stranded on its own row, because a
      grid container that is not filling its parent sizes its tracks to content
      and auto-fit never sees the width it needs. Ray had to ask three times.
      One card per column, however many cards there are, no guessing. */
   grid-template-columns: repeat(${({ $count }) => $count || 1}, minmax(0, 1fr));
   width: 100%;
   gap: 10px;
   /* Two up on a tablet, one on a phone — an operator reads this standing in
      the shop as often as at a desk. */
   @media (max-width: 900px) {
      grid-template-columns: 1fr 1fr;
   }
   @media (max-width: 560px) {
      grid-template-columns: 1fr;
   }
`;
const Card = styled.div<{ $tone: Tone }>`
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-left: 3px solid ${({ $tone }) => TONES[$tone]};
   border-radius: 8px;
   /* Tight, because four of these share one row and the third line is a full
      sentence. Ray asked for compact twice. */
   padding: 8px 10px;
   background: ${({ $tone }) => TONES[$tone]}0d;
   /* A grid track will not shrink below its content unless told to, which is
      what pushed the fourth card off the row in the first place. */
   min-width: 0;
`;
const CardLabel = styled.div`
   font-size: 10.5px;
   letter-spacing: 0.01em;
   opacity: 0.65;
   margin-bottom: 2px;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;
const CardNote = styled.div`
   margin-top: 2px;
   font-size: 10.5px;
   opacity: 0.7;
   line-height: 1.25;
   /* Long notes ("Offline since 8/24/2026, 12:41:29 PM") must wrap inside the
      column rather than widening it and pushing a card onto the next row. */
   overflow-wrap: anywhere;
`;
const CardValue = styled.div`
   font-size: 13px;
   font-weight: 600;
   line-height: 1.25;
   overflow-wrap: anywhere;
`;

const SubTitle = styled.h5`
   margin: 18px 0 8px;
   font-size: 12px;
   font-weight: 700;
   opacity: 0.75;
`;
const Notice = styled.p`
   margin: 12px 0 0;
   padding: 10px 12px;
   border-radius: 8px;
   background: rgba(192, 57, 43, 0.08);
   border: 1px solid rgba(192, 57, 43, 0.25);
   font-size: 13px;
   line-height: 1.45;
`;
const List = styled.ul`
   margin: 0;
   padding: 0;
   list-style: none;
   display: grid;
   gap: 8px;
`;
const Item = styled.li`
   display: flex;
   flex-wrap: wrap;
   gap: 4px 10px;
   font-size: 13px;
   line-height: 1.4;
`;
const Dot = styled.span<{ $tone: Tone }>`
   width: 8px;
   height: 8px;
   margin-top: 6px;
   border-radius: 50%;
   flex: 0 0 auto;
   background: ${({ $tone }) => TONES[$tone]};
`;
const When = styled.span`
   min-width: 170px;
   opacity: 0.6;
   font-variant-numeric: tabular-nums;
`;
// Colour carries meaning here, so it is never the ONLY signal — the sentence
// says the same thing in words, and the dot repeats it for a quick scan.
const What = styled.span<{ $tone: Tone }>`
   flex: 1;
   min-width: 200px;
   color: ${({ $tone }) => ($tone === 'ok' ? 'inherit' : TONES[$tone])};
   font-weight: ${({ $tone }) => ($tone === 'bad' ? 600 : 400)};
`;
const Empty = styled.p`
   margin: 6px 0 0;
   font-size: 13px;
   opacity: 0.7;
   line-height: 1.45;
`;
